/* ============================================================================
   Login do painel administrativo — fluxo em 2 passos.
   1) Solicita código por e-mail  →  2) código (OTP) + Admin ID.
   ============================================================================ */
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../services/api';
import { setSession, isAdmin } from '../lib/auth';
import { toast, ToastHost } from '../components/Toast';
import { CoffeeLogo, IconMail, IconKey, IconBack } from '../components/icons';
import './Login.css';

const CODE_LEN = 6;

export default function Login() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [adminId, setAdminId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isAdmin()) navigate('/treinamentos/manager/dashboard', { replace: true });
  }, [navigate]);

  const requestCode = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/auth/request-code', {}, false);
      if (res.success) {
        setEmail(res.data.email || 'admin');
        setStep(2);
        toast.success('Código enviado para o e-mail do administrador.');
      }
    } catch (err) {
      setError(err.message || 'Erro ao enviar o código.');
    } finally {
      setLoading(false);
    }
  };

  const login = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin/login', { email, code, adminId }, false);
      if (res.success) {
        setSession(res.data.token, res.data.user);
        navigate('/treinamentos/manager/dashboard', { replace: true });
      }
    } catch (err) {
      setError(err.message || 'Não foi possível entrar. Verifique o código e o Admin ID.');
    } finally {
      setLoading(false);
    }
  };

  const recover = async () => {
    setError('');
    setLoading(true);
    try {
      const res = await api.post('/admin/recover', {}, false);
      if (res.success) toast.success('Link de redefinição enviado para o e-mail do administrador.');
    } catch (err) {
      setError(err.message || 'Erro ao solicitar redefinição.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mg-auth">
      <div className="mg-auth-card">
        <div className="mg-auth-head">
          <span className="mg-auth-logo"><CoffeeLogo size={64} id="login" /></span>
          <h1>Painel Administrativo</h1>
          <p>Cloud Café &amp; DevOps · acesso restrito</p>
        </div>

        {error && <div className="mg-auth-error">{error}</div>}

        {step === 1 ? (
          <div className="mg-auth-body">
            <p className="mg-auth-text">
              Receba um código de acesso de uso único no e-mail cadastrado do administrador.
            </p>
            <button className="mg-btn mg-btn-primary mg-btn-block" onClick={requestCode} disabled={loading}>
              <IconMail /> {loading ? 'Enviando…' : 'Enviar código de acesso'}
            </button>
          </div>
        ) : (
          <form className="mg-auth-body" onSubmit={login}>
            <div className="mg-field">
              <label className="mg-label">Código de verificação</label>
              <OtpInput value={code} onChange={setCode} disabled={loading} />
            </div>

            <div className="mg-field">
              <label className="mg-label">ID de Administrador</label>
              <div className="mg-auth-input-icon">
                <IconKey size={17} />
                <input
                  type="password"
                  className="mg-input"
                  value={adminId}
                  onChange={(e) => setAdminId(e.target.value)}
                  placeholder="ID secreto"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <button
              type="submit"
              className="mg-btn mg-btn-primary mg-btn-block"
              disabled={loading || code.length < CODE_LEN}
            >
              {loading ? 'Entrando…' : 'Entrar no painel'}
            </button>

            <div className="mg-auth-links">
              <button type="button" className="mg-auth-link" onClick={() => { setStep(1); setCode(''); }} disabled={loading}>
                <IconBack size={14} /> Voltar
              </button>
              <button type="button" className="mg-auth-link" onClick={recover} disabled={loading}>
                Esqueci o Admin ID
              </button>
            </div>
          </form>
        )}

        <p className="mg-auth-note">
          Guarde seu Admin ID em local seguro. Não há recuperação automática por segurança.
        </p>
      </div>
      <ToastHost />
    </div>
  );
}

/* ── OTP: 6 caixas com auto-avanço e colar ── */
function OtpInput({ value, onChange, disabled }) {
  const refs = useRef([]);
  const digits = Array.from({ length: CODE_LEN }, (_, i) => value[i] || '');

  const setAt = (i, char) => {
    const next = digits.slice();
    next[i] = char;
    onChange(next.join('').slice(0, CODE_LEN));
  };

  const handleChange = (i, e) => {
    const char = e.target.value.replace(/\D/g, '').slice(-1);
    if (!char) return;
    setAt(i, char);
    if (i < CODE_LEN - 1) refs.current[i + 1]?.focus();
  };

  const handleKeyDown = (i, e) => {
    if (e.key === 'Backspace') {
      e.preventDefault();
      if (digits[i]) setAt(i, '');
      else if (i > 0) { setAt(i - 1, ''); refs.current[i - 1]?.focus(); }
    }
    if (e.key === 'ArrowLeft' && i > 0) refs.current[i - 1]?.focus();
    if (e.key === 'ArrowRight' && i < CODE_LEN - 1) refs.current[i + 1]?.focus();
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, CODE_LEN);
    if (pasted) { onChange(pasted); refs.current[Math.min(pasted.length, CODE_LEN - 1)]?.focus(); }
  };

  return (
    <div className="mg-otp" onPaste={handlePaste}>
      {digits.map((d, i) => (
        <input
          key={i}
          ref={(el) => (refs.current[i] = el)}
          className={`mg-otp-box${d ? ' is-filled' : ''}`}
          type="text"
          inputMode="numeric"
          maxLength={1}
          value={d}
          onChange={(e) => handleChange(i, e)}
          onKeyDown={(e) => handleKeyDown(i, e)}
          disabled={disabled}
          autoFocus={i === 0}
        />
      ))}
    </div>
  );
}
